<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface PanelProps {
	/** Header title text. Overridable via the `title` slot. */
	title?: string;
	/** Show the close button in the header. Emits `close` (consume as `@close`). */
	closable?: boolean;
	/** Accessible label and tooltip for the close button. */
	closeLabel?: string;
}

const props = defineProps<PanelProps>();

defineEmits<{
	close: [];
}>();

const { t } = useI18n();

const closeButtonLabel = computed(() => props.closeLabel ?? t('common.close'));
</script>

<template>
	<section class="flex h-full min-h-0 flex-col">
		<div class="flex h-(--secondary-header-height) shrink-0 items-center justify-between border-b px-5">
			<span class="block min-w-0 flex-1 truncate text-sm font-semibold">
				<slot name="title">{{ title }}</slot>
			</span>

			<div class="flex shrink-0 items-center justify-end gap-1">
				<slot name="actions" />

				<DTooltip v-if="closable" :text="closeButtonLabel">
					<DButton
						icon="lucide:x"
						size="sm"
						color="neutral"
						variant="ghost"
						square
						:aria-label="closeButtonLabel"
						@click="$emit('close')"
					/>
				</DTooltip>
			</div>
		</div>

		<slot />

		<div v-if="$slots.footer" class="shrink-0">
			<slot name="footer" />
		</div>
	</section>
</template>
